import { ChangeEvent, FC, PropsWithChildren, useCallback } from 'react';

import { RequisitesFieldConfig, RequisitesFieldName } from 'utils/constants/requisites';

import {
    Card,
    Cell,
    CellText,
    CheckableCard,
    FieldsGroupHelper,
    FormLabel,
    Radio,
    Text,
    useBreakpoint,
    VSpacing,
} from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import getFieldPath from 'components/RequisitesFormModal/getFieldPath';

import { Action } from 'store/createReducer';
import { setRequisiteByName, setRequisiteErrorByFieldPath } from 'store/models/requisites';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/RequisitesFormModal/RequisitesRadioField/RequisitesRadioField.module.less';

interface RequisitesRadioFieldProps extends RequisitesFieldConfig {
    isFieldDisabled: boolean;
    onChange: (fieldName: RequisitesFieldName, value: string) => void;
}

const RequisitesRadioField: FC<RequisitesRadioFieldProps & PropsWithChildren> = ({
    isFieldDisabled,
    fieldName,
    labelValues,
    values,
    error,
    onChange,
    tooltipContent,
    title,
    subtitle,
    label,
    dataQa,
}) => {
    const { requisites, requisitesErrors } = useSelector((state) => state.requisites);
    const dispatch = useDispatch();
    const trl = useTrls();

    const fieldValue = requisites[fieldName];

    const fieldPath = getFieldPath(fieldName);
    const fieldError = requisitesErrors[fieldName];
    const fieldErrorTrl = fieldError && error?.[fieldError];

    const handleChange = useCallback(
        ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
            const actions: Action[] = [setRequisiteByName({ fieldName, value })];
            if (fieldError) {
                actions.push(setRequisiteErrorByFieldPath({ fieldPath, value: null }));
            }
            dispatch(actions);
            if (onChange) {
                onChange(fieldName, value);
            }
        },
        [dispatch, fieldError, fieldName, fieldPath, onChange]
    );
    const { isMobile } = useBreakpoint();

    const content = (
        <div>
            {title && subtitle && (
                <>
                    <Text typography="title-4-semibold">{trl(title)}</Text>
                    <VSpacing default={4} />
                    <Text typography="paragraph-2-regular">{trl(subtitle)}</Text>
                    <VSpacing default={24} />
                </>
            )}
            {label && (
                <>
                    <FormLabel info={tooltipContent}>{trl(label)}</FormLabel>
                    <VSpacing default={12} />
                </>
            )}
            <FieldsGroupHelper invalid={!!fieldError} errorMessage={trl(fieldErrorTrl)}>
                <div className={styles.items} data-qa={dataQa}>
                    {labelValues &&
                        values?.map((value) => {
                            const radio = (
                                <Radio
                                    disabled={isFieldDisabled}
                                    value={value}
                                    onChange={handleChange}
                                    checked={fieldValue === value}
                                    invalid={!!fieldError}
                                />
                            );

                            return (
                                <div key={value} className={styles.item}>
                                    <CheckableCard
                                        disabled={isFieldDisabled}
                                        onChange={handleChange}
                                        checked={fieldValue === value}
                                        value={value}
                                        type="radio"
                                        padding={16}
                                        borderRadius={12}
                                        flexible
                                        data-qa={`${dataQa}-${value.toLowerCase()}`}
                                    >
                                        <Cell
                                            align="top"
                                            right={isMobile ? radio : undefined}
                                            left={isMobile ? undefined : radio}
                                        >
                                            <CellText>{trl(labelValues[value])}</CellText>
                                        </Cell>
                                    </CheckableCard>
                                </div>
                            );
                        })}
                </div>
            </FieldsGroupHelper>
        </div>
    );

    return title && !isMobile ? (
        <Card padding={24} borderRadius={24} stretched borderWidth="default">
            {content}
        </Card>
    ) : (
        content
    );
};

export default RequisitesRadioField;
